// src/components/AccountDrawer/MiniPortfolio/index.tsx
import { Trans } from '@lingui/macro'
import Column from 'components/Column'
import { AutoRow } from 'components/Row'
import { useAtomValue } from 'jotai/utils'
import { useState } from 'react'
import { shouldDisableNFTRoutesAtom } from 'state/application/atoms'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { ActivityTab } from './Activity'
import { PortfolioRowWrapper } from './PortfolioRow'
import QuestionHelper from 'components/QuestionHelper'

const Wrapper = styled(Column)`
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;

  ${PortfolioRowWrapper} {
    &:hover {
      background: ${({ theme }) => theme.hoverDefault};
    }
  }
`

const Nav = styled(AutoRow)`
  gap: 20px;
  align-items: center;
`

const NavItem = styled(ThemedText.SubHeader)<{ active?: boolean }>`
  color: ${({ theme, active }) => (active ? theme.textPrimary : theme.textTertiary)};
  transition: ${({ theme }) => `${theme.transition.duration.medium} ${theme.transition.timing.ease} color`};

  &:hover {
    ${({ theme, active }) => !active && `color: ${theme.textSecondary}`};
    cursor: pointer;
  }
`

const PageWrapper = styled.div`
  border-radius: 12px;
  margin-right: -16px;
  margin-left: -16px;
  width: calc(100% + 32px);
  flex: 1;
`

const TitleWithInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const InlineHelper = styled(QuestionHelper)`
  transform: translateY(1px);
  opacity: 0.85;
`

interface Page {
  title: React.ReactNode
  key: string
  component: ({ account }: { account: string }) => JSX.Element
}

const Pages: Array<Page> = [
  {
    title: (
      <TitleWithInfo>
        <Trans>Activity</Trans>
        <InlineHelper
          size={14}
          text={
            'These activities are stored locally in your browser and only include swaps executed via Piteas App on this device. They may not reflect your full on-chain history.'
          }
        />
      </TitleWithInfo>
    ),
    key: 'activity',
    component: ({ account }) => <ActivityTab account={account} />,
  },
]

export default function MiniPortfolio({ account }: { account: string }) {
  const [currentPage, setCurrentPage] = useState(0)
  const shouldDisableNFTRoutes = useAtomValue(shouldDisableNFTRoutesAtom)
  const Page = Pages[currentPage].component

  return (
    <Wrapper>
      <Nav>
        {Pages.map(({ title, key }, index) => {
          if (shouldDisableNFTRoutes && key.includes('nft')) return null
          return (
            <NavItem
              data-testid={`mini-portfolio-nav-${key}`}
              onClick={() => setCurrentPage(index)}
              active={currentPage === index}
              key={`Mini Portfolio page ${index}`}
            >
              {title}
            </NavItem>
          )
        })}
      </Nav>

      <PageWrapper>
        <Page account={account} />
      </PageWrapper>
    </Wrapper>
  )
}
